/**
 * Report formatting — layer 1 of 4.
 *
 *   plan JSON ──▶ derive ──▶ FACTS ──▶ rules ──▶ BLOCKS ──▶ renderer
 *                (arithmetic)        (sentences)        (layout only)
 *
 * This layer holds rupee, date, tenure and plural formatting and NOTHING ELSE.
 * It must never contain a condition about the plan. If a sentence needs a
 * different shape of a number, it belongs here; if it needs a different number,
 * that belongs in derive.
 *
 * Rules from the Dynamic Text Specification §5, each of which has produced a
 * bug at least once:
 *   • never render an array with .join(", ") — English needs a final "and"
 *   • counts up to ten read as words in prose, as figures in tables
 *   • fund names arrive SHOUTING and must be title-cased, acronyms preserved
 *   • money is en-IN grouped; above ten lakh switch to scale words
 *   • dates are "22 December 2028" in prose, "3 Jan 2029" in tables, never ISO
 *   • signed values use a true minus (−), never a hyphen
 */

/* ─────────────────────────────── numbers ────────────────────────────── */

const WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
];

/** Numbers up to ten read better as words in running text. */
export const words = (count: number): string =>
  count >= 0 && count <= 10 ? WORDS[count] : String(count);

/** "1 goal" / "3 goals" — the count as a figure with an agreeing noun. */
export const plural = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

/** "one goal" / "three goals" — the same, with the count as a word. */
export const pluralWords = (count: number, one: string, many: string): string =>
  `${words(count)} ${count === 1 ? one : many}`;

/** Just the agreeing noun, when the count is rendered separately. */
export const s = (count: number, one = "", many = "s"): string =>
  count === 1 ? one : many;

/**
 * "a, b and c". Never `.join(", ")` — that produces "a, b, c", which reads as
 * a truncated list. No serial comma; the report does not use one.
 */
export const list = (items: string[]): string => {
  const kept = items.filter(Boolean);
  if (kept.length === 0) return "";
  if (kept.length === 1) return kept[0];
  return `${kept.slice(0, -1).join(", ")} and ${kept[kept.length - 1]}`;
};

export const capitalise = (value: string): string =>
  value ? value[0].toUpperCase() + value.slice(1) : value;

/* ──────────────────────────────── money ─────────────────────────────── */

/** Indian digit grouping — 12,34,567 rather than 1,234,567. */
export const groupIN = (value: number, decimals = 0): string =>
  value.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * Money for running prose.
 *
 * FULL FIGURE, ALWAYS TWO DECIMALS. There used to be a magnitude rule here
 * that abbreviated above a crore — "₹4.93 crore" — on the reasoning that
 * nobody holds that many digits. The product's rule is the opposite: a figure
 * the client checks against a statement has to be the figure, so every amount
 * in every sentence carries its paise.
 */
export const money = (value: number): string => moneyExact(value);

/**
 * Exact rupees to the paise.
 *
 * `decimals` survives for a caller that genuinely wants none, but the default
 * is two and nothing in this feature overrides it.
 */
export const moneyExact = (value: number, decimals = 2): string => {
  const amount = Number(value) || 0;
  // A true minus, tight against the symbol — the same shape as `rupee` on the
  // screen, so a figure reads identically wherever it appears.
  const sign = amount < 0 ? "−" : "";
  return `${sign}₹${groupIN(Math.abs(amount), decimals)}`;
};

/** Two decimals — settlement figures checked against a trade-floor quote. */
export const moneyPaise = (value: number): string => moneyExact(value, 2);

/** Signed for a table: a true minus with a space, never a hyphen. */
/** Explicitly signed both ways, for a delta column. */
export const moneySigned = (value: number): string =>
  value < 0 ? moneyExact(value) : `+${moneyExact(value)}`;

export const percent = (value: number | null | undefined, decimals = 1): string =>
  value == null ? "—" : `${Number(value).toFixed(decimals)}%`;

/** "34%" — for shares where a decimal would be false precision. */
export const percentWhole = (value: number): string => `${Math.round(value)}%`;

/**
 * Percentage POINTS — a gap between two percentages. Deliberately unsuffixed:
 * "80% points above the ceiling" carries the unit twice and reads as a typo.
 */
export const points = (value: number): string => String(Math.round(value));

export const multiple = (value: number, decimals = 2): string =>
  `${value.toFixed(decimals)}×`;

/* ──────────────────────────── dates & tenure ────────────────────────── */

const parse = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value.includes("T") ? value : `${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "22 December 2028" — dates in prose. */
export const dateLong = (value?: string | null): string => {
  const date = parse(value);
  if (!date) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/** "3 Jan 2029" — dates in tables. */
export const dateShort = (value?: string | null): string => {
  const date = parse(value);
  if (!date) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/** "August 2036" — where the day is noise because the sentence is about a period. */
export const monthYear = (value?: string | null): string => {
  const date = parse(value);
  if (!date) return "—";
  return date.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

/**
 * "2 years 4 months" in prose, or "five years" where it is a round number
 * under ten. Tenure in a KPI tile uses `tenureShort`.
 */
export const tenureLong = (years: number, months: number): string => {
  if (months === 0 && years > 0 && years <= 10) {
    return `${words(years)} ${s(years, "year", "years")}`;
  }
  const parts: string[] = [];
  if (years > 0) parts.push(plural(years, "year", "years"));
  if (months > 0) parts.push(plural(months, "month", "months"));
  return parts.length ? parts.join(" ") : "no time at all";
};

/** "2y 4m" — the KPI-tile form. */
export const tenureShort = (years: number, months: number): string =>
  months > 0 ? `${years}y ${months}m` : `${years}y`;

/** "a fortnight" / "about three months" — a gap described rather than counted. */
export const gapInWords = (days: number): string => {
  if (days <= 1) return "a day";
  if (days <= 10) return `${words(days)} days`;
  if (days <= 20) return "a fortnight or so";
  if (days <= 45) return "about a month";
  const monthsApprox = Math.round(days / 30);
  return `about ${words(monthsApprox)} months`;
};

/* ───────────────────────────── proper nouns ─────────────────────────── */

const ACRONYMS = new Set([
  "IDCW",
  "SIP",
  "SWP",
  "AMC",
  "ELSS",
  "ETF",
  "HUF",
  "NFO",
  "PSU",
  "FMP",
  "REC",
  "NCD",
  "TDS",
  "YTM",
  "LTCG",
  "STCG",
  "US",
  "UK",
]);
const MINOR = new Set(["and", "of", "the", "a", "an", "for", "in", "to", "or"]);

/**
 * Fund names arrive from the inventory shouting —
 * "ADITYA BIRLA SUN LIFE BALANCED ADVANTAGE FUND - GROWTH OPTION" — and read
 * as an error printed that way inside a sentence.
 *
 * The mirror-image bug is lowercasing a proper noun to make it fit: a goal name
 * is the client's own words. Never derive a sentence form from a heading form
 * with .toLowerCase() — carry both strings instead.
 */
export const titleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/(\s+|-)/)
    .map((token, index) => {
      const bare = token.trim();
      if (!bare) return token;
      const upper = bare.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      if (index > 0 && MINOR.has(bare)) return bare;
      return bare[0].toUpperCase() + bare.slice(1);
    })
    .join("");

/** Trims a long fund or goal name for a heading without breaking a word. */
export const clamp = (value: string, max: number): string => {
  if (value.length <= max) return value;
  const cut = value.slice(0, max);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
};

/* ───────────────────────── instrument names ─────────────────────────── */

/**
 * Fund houses, as the API spells them against how a document should.
 *
 * The scheme master returns legal names in full caps — "ADITYA BIRLA SUN LIFE
 * BALANCED ADVANTAGE FUND - GROWTH OPTION". Printed verbatim they shout, they
 * wrap onto three lines in a table cell, and the part that identifies the fund
 * is buried behind twenty characters of fund-house name repeated on every row.
 *
 * The API name stays the record — it is what `orders[].isin` transacts against
 * and what appears in the mapping panels. This is presentation only.
 */
const HOUSE_ABBREVIATIONS: [RegExp, string][] = [
  [/^ADITYA BIRLA SUN LIFE\b/i, "ABSL"],
  [/^ICICI PRUDENTIAL\b/i, "ICICI Pru"],
  [/^HDFC\b/i, "HDFC"],
  [/^SBI\b/i, "SBI"],
  [/^KOTAK MAHINDRA\b/i, "Kotak"],
  [/^NIPPON INDIA\b/i, "Nippon India"],
  [/^AXIS\b/i, "Axis"],
  [/^MIRAE ASSET\b/i, "Mirae Asset"],
  [/^UTI\b/i, "UTI"],
  [/^DSP\b/i, "DSP"],
  [/^TATA\b/i, "Tata"],
  [/^FRANKLIN\b/i, "Franklin"],
  [/^BANDHAN\b/i, "Bandhan"],
  [/^EDELWEISS\b/i, "Edelweiss"],
  [/^INVESCO INDIA\b/i, "Invesco"],
  [/^CANARA ROBECO\b/i, "Canara Robeco"],
];

/** The plan variant, which the report has to distinguish — see `schemeName`. */
export type SchemePlan = "growth" | "idcw" | "unknown";

/**
 * Growth or dividend-payout, read off the scheme name.
 *
 * The API returns no structured field for this, and the distinction carries
 * real weight in the report: an IDCW plan distributes instead of compounding,
 * which quietly breaks the instalment arithmetic that assumed compounding.
 * Until `funds[].plan` exists this is the only way to know, so it is isolated
 * here rather than scattered through the copy — one function to delete when the
 * field arrives.
 */
export const schemePlan = (name: string): SchemePlan => {
  const upper = name.toUpperCase();
  if (/\bIDCW\b|\bDIVIDEND\b|\bPAYOUT\b/.test(upper)) return "idcw";
  if (/\bGROWTH\b/.test(upper)) return "growth";
  return "unknown";
};

/**
 * "ADITYA BIRLA SUN LIFE BALANCED ADVANTAGE FUND - GROWTH OPTION"
 *   → "ABSL Balanced Advantage Fund — Growth"
 *
 * The plan suffix is normalised rather than dropped: it is the difference
 * between a fund that compounds and one that does not.
 */
export const schemeName = (name: string): string => {
  let base = name.trim().replace(/\s+/g, " ");
  let house = "";
  for (const [pattern, short] of HOUSE_ABBREVIATIONS) {
    if (pattern.test(base)) {
      house = short;
      base = base.replace(pattern, "").trim();
      break;
    }
  }

  // Split the plan suffix off whatever separator the master happened to use:
  // " - GROWTH OPTION", "-GROWTH", " — IDCW PAYOUT".
  const plan = schemePlan(name);
  base = base
    .replace(/[-–—]?\s*(GROWTH|IDCW)\b.*$/i, "")
    .replace(/[-–—\s]+$/, "")
    .trim();

  const label = titleCase(base);
  const suffix =
    plan === "growth" ? " — Growth" : plan === "idcw" ? " — IDCW Payout" : "";
  return `${house ? `${house} ` : ""}${label}${suffix}`.trim();
};

/**
 * "7.79% REC Limited (P) Call : 31 Jan 2031" → "7.79% REC Limited — Call 31 Jan 2031"
 *
 * The trade desk's own string carries a private-placement marker and a colon
 * that reads as a typo in running text. The coupon, the issuer and the date all
 * survive — nothing identifying is removed.
 */
export const bondName = (name: string): string =>
  name
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\s*\(P\)\s*/i, " ")
    .replace(/\s*(Call|Put|Maturity)\s*:\s*/i, " — $1 ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * The frequency as a NOUN, for "₹2,00,000 a month".
 *
 * `swp.freq` and `sipFreq` come back as adjectives — "Monthly", "Half-Yearly" —
 * and lowercasing an adjective into a noun slot produces "a monthly", which is
 * the kind of error that makes a document read as machine-written.
 */
export const periodNoun = (frequency: string): string =>
  ({
    monthly: "month",
    quarterly: "quarter",
    "half-yearly": "half-year",
    "half yearly": "half-year",
    yearly: "year",
    annual: "year",
    annually: "year",
  })[frequency.trim().toLowerCase()] ?? frequency.toLowerCase();

/**
 * "Sep 26" — the compact form for a range inside a caption.
 *
 * `monthYear` spells the month for running text; a KPI caption has a quarter of
 * a page's width and two of these in it, so the range needs the short form or
 * it truncates.
 */
export const monthYearShort = (value?: string | null): string => {
  const date = parse(value);
  if (!date) return "—";
  // en-IN renders September as "Sept"; a three-letter month keeps a two-date
  // range inside a quarter-width caption.
  const month = date.toLocaleString("en-IN", { month: "short" }).slice(0, 3);
  return `${month} ${String(date.getFullYear()).slice(2)}`;
};
